import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { registerSchema as sharedRegisterSchema } from '@fixtyer/shared';
import { useAuth } from '@/contexts/auth-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { trackSignUp } from '@/lib/analytics';
import { isSafeContinuation } from '@/lib/guest-draft';

// Shared schema (matches the API's rules) plus a confirm field for the form.
const registerSchema = sharedRegisterSchema
  .extend({
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type RegisterForm = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const continuation = isSafeContinuation(new URLSearchParams(search).get('continue'));
  const { register: registerUser, isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  // Already signed in - no need to show the registration form
  if (isAuthenticated && !isLoading) {
    return <Navigate to={continuation} replace />;
  }

  const onSubmit = async (data: RegisterForm) => {
    setError(null);
    setIsLoading(true);

    try {
      await registerUser(data.email, data.password, data.name);
      trackSignUp();
      // Straight into the claim wizard — the dashboard is a dead end for a
      // brand-new user and was our single biggest funnel drop-off point.
      navigate(continuation, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-16rem)] flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-bold text-brand-950">Create your account</h1>
          <p className="mt-2 text-surface-800">
            Start preparing your pothole damage claim
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-brand-900/5 border border-surface-200 p-8">
          {error && (
            <div className="mb-6 p-4 bg-error-500/10 border border-error-500/20 rounded-xl text-error-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Input
              label="Full name"
              type="text"
              autoComplete="name"
              error={errors.name?.message}
              {...register('name')}
            />

            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              error={errors.email?.message}
              {...register('email')}
            />

            <Input
              label="Password"
              type="password"
              autoComplete="new-password"
              error={errors.password?.message}
              {...register('password')}
            />

            <Input
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />

            <div className="text-sm text-surface-800">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-brand-600 hover:text-brand-700">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-brand-600 hover:text-brand-700">
                Privacy Policy
              </Link>
              .
            </div>

            <Button type="submit" fullWidth loading={isLoading}>
              Create account
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-surface-800">Already have an account? </span>
            <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
